import React, { useEffect, useState, useCallback } from "react";
import {
  Sparkles, RefreshCw, TrendingUp, AlertTriangle, Lightbulb, Target,
  ThumbsUp, ThumbsDown, Minus, Copy, Wand2, MessageSquareQuote,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { fmtDateTime } from "../lib/time";

const fmtTime = (iso) => (iso ? fmtDateTime(iso) : "");

export function AiCard({ title, subtitle, generatedAt, cached, onRefresh, loading, children, testId }) {
  return (
    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 to-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#4338CA] text-white shadow-sm">
            <Sparkles className="h-4.5 w-4.5" />
          </span>
          <div>
            <h3 className="font-head text-base font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid={`${testId}-refresh`}
              className="border-indigo-200 text-[#4338CA] hover:bg-indigo-50">
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Thinking…" : "Refresh"}
            </Button>
          )}
          {generatedAt && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid={`${testId}-last`}>
              Last refreshed · {fmtTime(generatedAt)}
              {cached && <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700">cached</span>}
            </span>
          )}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Thinking({ label = "Analysing your data…" }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-slate-500" data-testid="ai-thinking">
      <Wand2 className="h-4 w-4 animate-pulse text-[#4338CA]" /> {label}
    </div>
  );
}

function List({ icon: Icon, color, items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2 text-sm text-slate-700">
          <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function useAi(fetcher, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const run = useCallback(async (refresh = false) => {
    setLoading(true);
    try { setData(await fetcher(refresh)); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { run(false); }, [run]);
  return { data, loading, refresh: () => run(true) };
}

/* ---------------- Executive Brief ---------------- */
export function ExecutiveBrief({ period = "monthly", locationId }) {
  const { data, loading, refresh } = useAi(
    async (refresh) => (await api.get(`/ai/executive-brief`, { params: { period, location_id: locationId, refresh } })).data,
    [period, locationId]
  );
  const p = data?.payload;
  return (
    <AiCard title="AI Executive Brief" subtitle="Your performance, explained and actioned"
      generatedAt={data?.generated_at} cached={data?.cached} onRefresh={refresh} loading={loading} testId="ai-exec-brief">
      {loading && !p ? <Thinking /> : p && (
        <div className="space-y-4">
          {p.headline && <p className="text-base font-semibold text-slate-900" data-testid="brief-headline">{p.headline}</p>}
          {p.summary && <p className="text-sm leading-relaxed text-slate-600">{p.summary}</p>}
          <div className="grid gap-4 md:grid-cols-2">
            {p.wins?.length > 0 && <Block title="Wins" icon={TrendingUp} color="#10B981"><List icon={ThumbsUp} color="#10B981" items={p.wins} /></Block>}
            {p.risks?.length > 0 && <Block title="Risks" icon={AlertTriangle} color="#EF4444"><List icon={AlertTriangle} color="#EF4444" items={p.risks} /></Block>}
          </div>
          {p.recommendations?.length > 0 && (
            <Block title="Recommended actions" icon={Lightbulb} color="#4338CA">
              <div className="space-y-2">
                {p.recommendations.map((r, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`brief-rec-${i}`}>
                    <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                    {r.detail && <p className="text-xs text-slate-500">{r.detail}</p>}
                  </div>
                ))}
              </div>
            </Block>
          )}
          {p.focus?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Target className="h-4 w-4 text-[#4338CA]" />
              {p.focus.map((f, i) => <span key={i} className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-[#4338CA]">{f}</span>)}
            </div>
          )}
        </div>
      )}
    </AiCard>
  );
}

function Block({ title, icon: Icon, color, children }) {
  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color }}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      {children}
    </div>
  );
}

/* ---------------- Comment Insights ---------------- */
export function CommentInsights({ locationId }) {
  const { data, loading, refresh } = useAi(
    async (refresh) => (await api.get(`/ai/comment-insights`, { params: { location_id: locationId, refresh } })).data,
    [locationId]
  );
  const p = data?.payload;
  const s = p?.sentiment || {};
  return (
    <AiCard title="Comment Themes & Sentiment" subtitle="What customers are telling you"
      generatedAt={data?.generated_at} cached={data?.cached} onRefresh={refresh} loading={loading} testId="ai-comments">
      {loading && !p ? <Thinking label="Reading customer comments…" /> : p?.empty ? (
        <p className="py-6 text-sm text-slate-500">No customer comments in the last 90 days yet. As comments come in, themes and sentiment will appear here.</p>
      ) : p && (
        <div className="space-y-4">
          <p className="text-xs text-slate-400">{p.count} comments analysed</p>
          <div className="flex overflow-hidden rounded-full" data-testid="sentiment-bar">
            <SentSeg label="Positive" pct={s.positive} color="#10B981" icon={ThumbsUp} />
            <SentSeg label="Neutral" pct={s.neutral} color="#94A3B8" icon={Minus} />
            <SentSeg label="Negative" pct={s.negative} color="#EF4444" icon={ThumbsDown} />
          </div>
          {p.summary && <p className="text-sm leading-relaxed text-slate-600">{p.summary}</p>}
          {p.themes?.length > 0 && (
            <div className="space-y-2">
              {p.themes.map((t, i) => (
                <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`theme-${i}`}>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t.label}</p>
                    {t.example && <p className="mt-0.5 text-xs italic text-slate-400">"{t.example}"</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-sm font-bold text-slate-700">{t.mentions}</span>
                    <p className="text-[10px] font-semibold uppercase" style={{ color: t.sentiment === "positive" ? "#10B981" : t.sentiment === "negative" ? "#EF4444" : "#94A3B8" }}>{t.sentiment}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {p.recommendations?.length > 0 && <Block title="Recommended actions" icon={Lightbulb} color="#4338CA"><List icon={Lightbulb} color="#4338CA" items={p.recommendations} /></Block>}
        </div>
      )}
    </AiCard>
  );
}

function SentSeg({ label, pct, color, icon: Icon }) {
  const v = Number(pct) || 0;
  if (v <= 0) return null;
  return (
    <div className="flex h-7 items-center justify-center gap-1 text-[11px] font-bold text-white" style={{ width: `${v}%`, background: color }} title={`${label} ${v}%`}>
      {v >= 12 && <Icon className="h-3 w-3" />}{v >= 8 ? `${v}%` : ""}
    </div>
  );
}

/* ---------------- Employee Coaching ---------------- */
export function CoachingCard({ employeeId }) {
  const { data, loading, refresh } = useAi(
    async (refresh) => (await api.get(`/ai/employee-coaching/${employeeId}`, { params: { refresh } })).data,
    [employeeId]
  );
  const p = data?.payload;
  return (
    <AiCard title="AI Coaching" subtitle="Personalised growth, from real ratings"
      generatedAt={data?.generated_at} cached={data?.cached} onRefresh={refresh} loading={loading} testId="ai-coaching">
      {loading && !p ? <Thinking label="Preparing your coaching…" /> : p && (
        <div className="space-y-4">
          {p.cv_narrative && <p className="rounded-lg bg-white p-3 text-sm italic leading-relaxed text-slate-700" data-testid="coaching-narrative">{p.cv_narrative}</p>}
          <div className="grid gap-4 md:grid-cols-2">
            {p.strengths?.length > 0 && <Block title="Strengths" icon={ThumbsUp} color="#10B981"><List icon={ThumbsUp} color="#10B981" items={p.strengths} /></Block>}
            {p.improvements?.length > 0 && <Block title="Grow here" icon={Target} color="#F59E0B"><List icon={Target} color="#F59E0B" items={p.improvements} /></Block>}
          </div>
          {p.tips?.length > 0 && (
            <Block title="Quick wins" icon={Lightbulb} color="#4338CA">
              <div className="space-y-2">
                {p.tips.map((t, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`tip-${i}`}>
                    <p className="text-sm font-semibold text-slate-800">{t.title}</p>
                    {t.detail && <p className="text-xs text-slate-500">{t.detail}</p>}
                  </div>
                ))}
              </div>
            </Block>
          )}
        </div>
      )}
    </AiCard>
  );
}

/* ---------------- Service Recovery Draft (on-demand) ---------------- */
export function RecoveryDraft({ caseId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const gen = async (refresh = false) => {
    setLoading(true);
    try { setData((await api.post(`/ai/recovery-draft/${caseId}`, null, { params: { refresh } })).data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const copy = () => {
    const t = data?.payload?.customer_message || "";
    navigator.clipboard?.writeText(t); toast.success("Reply copied");
  };
  const p = data?.payload;

  if (!data && !loading) {
    return (
      <Button onClick={() => gen(false)} data-testid="ai-recovery-generate" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
        <Sparkles className="mr-1.5 h-4 w-4" /> Draft a recovery reply with AI
      </Button>
    );
  }
  return (
    <AiCard title="AI Recovery Assistant" subtitle="A reply + action plan, ready to use"
      generatedAt={data?.generated_at} onRefresh={() => gen(true)} loading={loading} testId="ai-recovery">
      {loading && !p ? <Thinking label="Drafting a thoughtful reply…" /> : p && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="rounded-full px-2 py-0.5 font-bold uppercase" style={{
              background: p.severity === "high" ? "#FEE2E2" : p.severity === "low" ? "#DCFCE7" : "#FEF3C7",
              color: p.severity === "high" ? "#DC2626" : p.severity === "low" ? "#16A34A" : "#D97706",
            }}>{p.severity} severity</span>
            {p.likely_root_cause && <span className="text-slate-500">{p.likely_root_cause}</span>}
          </div>
          {p.customer_message && (
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#4338CA]"><MessageSquareQuote className="h-3.5 w-3.5" /> Draft reply</p>
                <Button variant="ghost" size="sm" onClick={copy} data-testid="copy-recovery" className="h-7 text-[#4338CA]"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" data-testid="recovery-message">{p.customer_message}</p>
            </div>
          )}
          {p.internal_steps?.length > 0 && <Block title="Action plan" icon={Target} color="#4338CA"><List icon={Lightbulb} color="#4338CA" items={p.internal_steps} /></Block>}
          {p.goodwill_suggestion && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800"><b>Goodwill idea:</b> {p.goodwill_suggestion}</p>}
        </div>
      )}
    </AiCard>
  );
}
